import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaFire, FaVideo } from 'react-icons/fa';
import { useArticles } from '../context/ArticleContext';
import SEO from '../components/SEO';
import RelativeTime from '../components/RelativeTime';
import { rankAndSelect } from '../utils/homepageRanker';
import { safeAlt } from '../utils/seoUtils';
import './HomePage.css';


function HomePage() {
  const { featuredArticles, articles, loading, error, fetchArticles, fetchFeaturedArticles } = useArticles();

  const handleRetry = () => {
    fetchArticles();
    fetchFeaturedArticles();
  };

  const { hero, topStories, latest } = useMemo(
    () => rankAndSelect(articles, featuredArticles),
    [articles, featuredArticles]
  );

  // Create unique article pools for each section
  const sectionArticles = useMemo(() => {
    console.log('Total articles available:', articles.length);
    console.log('Hero article:', hero ? hero.id : 'none');
    
    // Use only the main articles array (which has all 18+ articles)
    const availableArticles = hero ? articles.filter(a => a.id !== hero.id) : articles;
    
    console.log('Available articles after hero removal:', availableArticles.length);
    
    // Ensure we have enough articles for all sections
    const sidebarLatest = availableArticles.slice(0, Math.min(4, availableArticles.length));
    const trending = availableArticles.slice(4, Math.min(8, availableArticles.length));
    const streaming = availableArticles.slice(8, Math.min(12, availableArticles.length));
    
    console.log('Section distributions:', {
      sidebarLatest: sidebarLatest.length,
      trending: trending.length,
      streaming: streaming.length
    });
    
    return {
      sidebarLatest,
      trending,
      streaming
    };
  }, [hero, articles]);

  if (loading) {
    return (
      <main className="home">
        <div className="loading">Loading latest entertainment news...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="home">
        <SEO 
          title="Entertainment News & Film Festival Coverage"
          description="Stay updated with film, TV, festivals, awards, and reviews from E Media Forum."
          type="website"
          url="https://emediaforum.com"
        />
        <div className="error-container" style={{
          textAlign: 'center',
          padding: '2rem',
          maxWidth: '600px',
          margin: '2rem auto'
        }}>
          <h2>Unable to Load Articles</h2>
          <p>{error}</p>
          <button 
            onClick={handleRetry}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007cba',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              marginTop: '1rem'
            }}
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="home">
      <SEO 
        title="Entertainment News & Film Festival Coverage"
        description="Stay updated with film, TV, festivals, awards, and reviews from E Media Forum."
        type="website"
        url="https://emediaforum.com"
        image={(hero && hero.image) || undefined}
        imageAlt={hero ? safeAlt({
          title: hero.title,
          category: hero.category,
          authorByline: hero.authorByline,
          author: hero.author,
        }) : undefined}
      />

      <div className="homepage-layout">
        {/* LEFT COLUMN - FEATURED ARTICLE */}
        <div className="main-content">
          {hero && (
            <section className="featured-article">
              <Link to={`/article/${hero.id}`} className="featured-link">
                <div className="featured-image-container">
                  <img
                    src={hero.image}
                    alt={safeAlt({
                      title: hero.title,
                      category: hero.category,
                      authorByline: hero.authorByline,
                      author: hero.author,
                    })}
                    className="featured-image"
                  />
                  <div className="see-it-big">SEE IT BIG</div>
                </div>
                <div className="featured-content">
                  <div className="featured-category">{hero.category}</div>
                  <h1 className="featured-title">{hero.title}</h1>
                  {hero.subtitle && (
                    <p className="featured-subtitle">{hero.subtitle}</p>
                  )}
                  <div className="featured-meta">
                    <span className="featured-author">BY {hero.author?.toUpperCase() || 'STAFF'}</span>
                  </div>
                </div>
              </Link>
            </section>
          )}
        </div>

        {/* RIGHT COLUMN - LATEST NEWS SIDEBAR */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h2 className="sidebar-title">Latest News</h2>
          </div>
          <div className="sidebar-articles">
            {sectionArticles.sidebarLatest.map((article, index) => (
              <Link key={`sidebar-${article.id}`} to={`/article/${article.id}`} className="sidebar-article">
                <div className="sidebar-category">{article.category}</div>
                <h3 className="sidebar-article-title">{article.title}</h3>
                <div className="sidebar-meta">
                  <RelativeTime 
                    date={article.createdAt || article.publishTime || article.date} 
                    className="sidebar-time"
                  />
                </div>
              </Link>
            ))}
            <div className="more-news-btn">
              <button className="more-news-link" onClick={() => document.getElementById('more-articles').scrollIntoView({behavior: 'smooth'})}>MORE NEWS →</button>
            </div>
          </div>
        </div>
      </div>

      {/* MORE ARTICLES SECTIONS */}
      <div id="more-articles" className="more-articles-section">
        {/* TRENDING NOW SECTION */}
        <section className="article-section">
          <div className="section-header">
            <h2 className="section-title">Trending Now <FaFire className="section-icon" /></h2>
            <p className="section-subtitle">Discover the hottest entertainment stories everyone's talking about right now.</p>
          </div>
          <div className="articles-row">
            {sectionArticles.trending.map(article => (
              <Link key={`trending-${article.id}`} to={`/article/${article.id}`} className="article-card">
                <div className="card-image-container">
                  <img
                    src={article.image}
                    alt={safeAlt({
                      title: article.title,
                      category: article.category,
                      authorByline: article.authorByline,
                      author: article.author,
                    })}
                    className="card-image"
                  />
                </div>
                <div className="card-content">
                  <div className="card-category">{article.category}</div>
                  <h3 className="card-title">{article.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* STREAMING SPOTLIGHT SECTION */}
        <section className="article-section">
          <div className="section-header">
            <h2 className="section-title">Streaming Spotlight <FaVideo className="section-icon" /></h2>
            <p className="section-subtitle">The latest from Netflix, Disney+, Amazon Prime, and all your favorite platforms.</p>
          </div>
          <div className="articles-row">
            {sectionArticles.streaming.map(article => (
              <Link key={`streaming-${article.id}`} to={`/article/${article.id}`} className="article-card">
                <div className="card-image-container">
                  <img
                    src={article.image}
                    alt={safeAlt({
                      title: article.title,
                      category: article.category,
                      authorByline: article.authorByline,
                      author: article.author,
                    })}
                    className="card-image"
                  />
                </div>
                <div className="card-content">
                  <div className="card-category">{article.category}</div>
                  <h3 className="card-title">{article.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>


      </div>
    </main>
  );
}

export default HomePage;